import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { TextField, FormHelperText } from "@mui/material";
import useKeyboard from "@shared/modules/utils/Keyboard/useKeyboard";
import EventService from "@shared/services/EventService";
import { scrollFieldIntoViewIfHidden } from "./constants";

const CardInputField = forwardRef(({
  label,
  errorMsg,
  value = "",
  onChangeInput,
  containerClass,
  showKeyboard = true,
  virtualKeyboardLayout = "number3",
  virtualKeyboardLang = "en2",
  length = 4, // Total number of fields
  onFieldFocusChange,
  onAllFieldsFilled,
  enableScrollFieldIfHiddenEvent = true,
  onChange = () => {},
  themeName="numberic",
  ...restProps
}, ref) => {
  const [cardValues, setCardValues] = useState(Array(length).fill(""));
  const [focusIndex, setFocusIndex] = useState(null);  // Track the currently focused field
  const [keyboardVisible, setKeyboardVisible] = useState(false);  // Track keyboard visibility
  const [keyboardTimer, setKeyboardTimer] = useState(null); // Debounce for keyboard visibility
  const [focus, setFocus] = useState(false);
  // Create a ref for each field
  const inputRefs = useRef(cardValues.map(() => React.createRef()));

  const focusRef = useRef(false); //focusIndex state is "stale" in subscribe event

  const { openKeyboard, closeKeyboard } = useKeyboard(value, (updatedValue) => handleVirtualKeyboardChange(updatedValue));

  useImperativeHandle(ref, () => ({
    focus: (index = 0) => {
      if (
        index >= 0 &&
        index < length &&
        inputRefs.current[index]?.current
      ) {
        handleFocus(index);
      }
    }
  }));

  useEffect(() => {
    let eventInput = EventService.subscribe("keypad.blur", bool => {
      setKeyboardVisible(false); // Hide keyboard on blur if necessary
      setFocus(!bool)
    });
    let eventInput2 = EventService.subscribe("keypad.focus", () => {
      setKeyboardVisible(true); // Show keyboard on focus
    });

    let scrollEvent = enableScrollFieldIfHiddenEvent
      ? EventService.subscribe("keyboard.show", () => {
          if (focusRef.current) {
              scrollFieldIntoViewIfHidden(inputRefs.current["1"]);
          }
      })
      : {};

    if(!!value) handleVirtualKeyboardChange(value);
    if(!!restProps?.defaultValue) handleVirtualKeyboardChange(restProps?.defaultValue);

    return () => {
      eventInput.remove();
      eventInput2.remove();
      if (scrollEvent.remove) scrollEvent.remove();
    };
  }, []);

  const handleChange = (e, index) => {
    const sanitizedValue = e.target.value.replace(/\D/g, ""); // Allow only digits
    if (sanitizedValue.length <= 4) {
      const updatedCardValues = [...cardValues];
      updatedCardValues[index] = sanitizedValue;
      // Ensure total value doesn't exceed 16 digits
      const combinedValue = updatedCardValues.join("");
      if (combinedValue.length <= 16) {
        setCardValues(updatedCardValues);
        onChangeInput(combinedValue);
        onChange(combinedValue);
      }
    }
  };

  const handleVirtualKeyboardChange = (combinedValue) => {
    // Ensure total value doesn't exceed 16 digits
    if (combinedValue.length <= 16) {
      const splitValues = [];
      for (let i = 0; i < length; i++) {
        splitValues.push(combinedValue.slice(i * 4, i * 4 + 4)); // Split into groups of 4
      }
      setCardValues(splitValues);
      const activeInd = splitValues.findIndex(val => val.length <= length && val.length !== 0);
      handleFocus(activeInd >= 0 ? activeInd : null);
      onChangeInput(combinedValue); // Only send combined value if it's valid
    }
  };

  const handleFocus = (index) => {
    setFocusIndex(index);
    focusRef.current = true;

    if (cardValues[index]?.trim() === "" && typeof onFieldFocusChange === "function") {
      onFieldFocusChange(index);
    }

    if (showKeyboard && !keyboardVisible) {
      openKeyboard({
        layoutName: virtualKeyboardLayout,
        virtualKeyboardLang,
        themeName:themeName,
      });
      setKeyboardVisible(true); // Ensure the keyboard stays open when focusing
      setFocus(true)
    }

    // Focus the specific field when its index is focused
    if (inputRefs.current[index]) {
      inputRefs.current[index].current?.focus();
    }
  };

  const handleBlur = () => {
    focusRef.current = false;
    // setFocus(false)
    // Delay the hide of the keyboard if there is still focus on any field
    if (keyboardTimer) clearTimeout(keyboardTimer);
    const allFilled = cardValues.every(val => val.length === 4);
    if (allFilled && typeof onAllFieldsFilled === "function") {
      onAllFieldsFilled(cardValues.join(""));
    }
    setKeyboardTimer(setTimeout(() => {
      if (focusIndex === null) {
        closeKeyboard();
        setKeyboardVisible(false); // Only hide the keyboard if no field is focused
      }
    }, 300)); // Short delay to ensure blur event is not triggered too soon
  };

  return (
    <div
      className={`input-field-sec card-fields ${errorMsg ? "error-field" : ""} ${focus ? "blurFocus" : ""} ${containerClass || ""} ${label && "with-label"}`}
    >
      {cardValues.map((fieldValue, index) => (
        <div key={index} style={{ display: "flex", alignItems: "center", height: "120px" }}>
          <TextField
            ref={inputRefs.current[index]} // Set the ref for each field
            value={fieldValue}
            onFocus={() => handleFocus(index)}
            autoFocus={false}
            focused={fieldValue.length === 4 || index === focusIndex}
            onBlur={handleBlur}
            inputProps={{
              maxLength: 4,
              style: { textAlign: "center" },
            }}
            variant="outlined"
            {...restProps}
            onChange={(e) => handleChange(e, index)}
          />
          {index < cardValues.length - 1 && (
            <span style={{ margin: "0 8px", fontSize: "1.5rem", color: "#aaa" }}>
              -
            </span>
          )}
        </div>
      ))}
      {label && <FormHelperText>{label}</FormHelperText>}
      {errorMsg && <span className="helperTextError">{errorMsg}</span>}
    </div>
  );
});

export default CardInputField;
